import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  Home,
  ChevronLeft,
  ChevronRightIcon,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  TrendingUp,
  BarChart3,
  Building2,
} from "lucide-react";
import { Link } from "react-router-dom";
import ContratosService from "../services/contratosCofresService.tsx";
import Loading from "./Loading.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

type Contrato = {
  id: number;
  socioId?: number;
  socioNombre?: string;
  cajaNumero?: string | number;
  estado?: string;
  monto?: number;
  saldoPendiente?: number;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  raw?: any;
};

const ITEMS_PER_PAGE = 12;

type ResumenCard = {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  tone: "rose" | "amber" | "emerald" | "indigo";
};

const normalizarEstado = (estado?: string) => {
  const est = (estado || "").toLowerCase();
  if (est.includes("venc")) return "Vencido";
  if (est.includes("final") || est.includes("cancel")) return "Finalizado";
  if (est.includes("pend") || est.includes("adeud")) return "Pendiente";
  if (est.includes("activo") || est.includes("vigente")) return "Activo";
  return estado || "Activo";
};

const formatMoney = (n?: number) =>
  typeof n === "number" ? `$${n.toLocaleString("es-AR")}` : "—";

const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

export default function ContratosDashboard() {
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [estadoFiltro, setEstadoFiltro] = useState<string>("todos");
  const [busqueda, setBusqueda] = useState("");
  const { getAccessTokenSilently } = useAuth0();
  const [anioPagos, setAnioPagos] = useState<number>(new Date().getFullYear());
  const [periodoDeuda, setPeriodoDeuda] = useState<number | "">("");
  const [resumenSocios, setResumenSocios] = useState<any | null>(null);
  const [categoriasSocios, setCategoriasSocios] = useState<any[] | null>(null);
  const [pagosVsImpagos, setPagosVsImpagos] = useState<any[] | null>(null);
  const [totalRecaudado, setTotalRecaudado] = useState<any | null>(null);
  const [cajasResumen, setCajasResumen] = useState<any | null>(null);
  const [ocupacionPorTamano, setOcupacionPorTamano] = useState<any[] | null>(null);
  const [porcentajeDeuda, setPorcentajeDeuda] = useState<any | null>(null);
  const [deudaPorMes, setDeudaPorMes] = useState<any[] | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const storedSocio = localStorage.getItem("socioSeleccionado");
        let data: Contrato[] = [];

        if (storedSocio) {
          const socio = JSON.parse(storedSocio);
          const socioId = socio?.socio_id || socio?.id;
          if (socioId) {
            data = await ContratosService.getContratosBySocio(Number(socioId), token);
          }
        }

        // Si no hay socio específico, cargar todos
        if (!data.length) {
          data = await ContratosService.getContratos(token);
        }

        setContratos(data);
      } catch (err: any) {
        setError(err?.message || "No se pudieron cargar los contratos.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const fetchDashboard = async () => {
      setDashboardLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const base = process.env.REACT_APP_BACK_URL || "";
        const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
        const fetchJson = async (path: string) => {
          const res = await fetch(`${base}${path}`, { headers });
          if (!res.ok) throw new Error(`Error ${res.status}`);
          return res.json();
        };
        const [resSocios, resCategorias, resPagos, resTotal, resCajas, resOcupacion, resPctDeuda, resDeudaMes] =
          await Promise.all([
          fetchJson("/dashboard/socios/resumen").catch(() => fetchJson("/dashboard/socios-resumen")),
          fetchJson("/dashboard/socios/por-categoria").catch(() =>
            fetchJson("/dashboard/socios-por-categoria")
          ),
          fetchJson(`/dashboard/pagos/pagos-vs-impagos/${anioPagos}`).catch(() =>
            fetchJson(`/dashboard/pagos-vs-impagos/${anioPagos}`)
          ),
          fetchJson("/dashboard/pagos/total-recaudado").catch(() =>
            fetchJson("/dashboard/total-recaudado")
          ),
          fetchJson("/dashboard/cajas/resumen").catch(() => fetchJson("/dashboard/cajas-resumen")),
          fetchJson("/dashboard/cajas/ocupacion-por-tamano").catch(() =>
            fetchJson("/dashboard/cajas-ocupacion-por-tamano")
          ),
          fetchJson(
            `/dashboard/pagos/porcentaje-deuda?anio=${anioPagos}&periodo=${periodoDeuda}`
          ).catch(() =>
            fetchJson(`/dashboard/porcentaje-deuda?anio=${anioPagos}&periodo=${periodoDeuda}`)
          ),
          fetchJson(`/dashboard/pagos/deuda-por-mes/${anioPagos}`).catch(() =>
            fetchJson(`/dashboard/deuda-por-mes/${anioPagos}`)
          ),
        ]);
        setResumenSocios(resSocios);
        setCategoriasSocios(Array.isArray(resCategorias) ? resCategorias : null);
        setPagosVsImpagos(Array.isArray(resPagos) ? resPagos : null);
        setTotalRecaudado(resTotal);
        setCajasResumen(resCajas);
        setOcupacionPorTamano(Array.isArray(resOcupacion) ? resOcupacion : null);
        setPorcentajeDeuda(resPctDeuda);
        setDeudaPorMes(Array.isArray(resDeudaMes) ? resDeudaMes : null);
      } catch (err) {
        console.warn("No se pudo cargar el dashboard de socios/pagos", err);
      } finally {
        setDashboardLoading(false);
      }
    };
    fetchDashboard();
  }, [anioPagos, periodoDeuda, getAccessTokenSilently]);

  const enriched = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return contratos.map((c) => {
      const estadoNorm = normalizarEstado(c.estado);
      const fechaFinObj = c.fechaFin ? new Date(c.fechaFin) : null;
      const vencido =
        !!fechaFinObj &&
        !isNaN(fechaFinObj.getTime()) &&
        fechaFinObj.getTime() < hoy.getTime() &&
        estadoNorm !== "Finalizado";
      const diffDias = fechaFinObj
        ? Math.ceil((fechaFinObj.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24))
        : null;
      const proximo = diffDias !== null && diffDias >= 0 && diffDias <= 30;
      return {
        ...c,
        estadoNorm,
        vencido,
        proximo,
        diffDias,
      };
    });
  }, [contratos]);

  const filtrados = useMemo(() => {
    return enriched.filter((c) => {
      const matchEstado = estadoFiltro === "todos" ? true : c.estadoNorm === estadoFiltro;
      const term = busqueda.trim().toLowerCase();
      const matchSearch = term
        ? (c.socioNombre || "").toLowerCase().includes(term) ||
          String(c.socioId || "").includes(term) ||
          String(c.cajaNumero || "").includes(term)
        : true;
      return matchEstado && matchSearch;
    });
  }, [enriched, estadoFiltro, busqueda]);

  const currentContratos = filtrados.slice(startIndex, endIndex);
  const totalPages = Math.max(1, Math.ceil(filtrados.length / ITEMS_PER_PAGE));

  const stats = useMemo(() => {
    const totalMonto = enriched.reduce((acc, c) => acc + (c.monto || 0), 0);
    const totalSaldo = enriched.reduce((acc, c) => acc + (c.saldoPendiente || 0), 0);
    const vencidos = enriched.filter((c) => c.vencido);
    const proximos = enriched.filter((c) => c.proximo && !c.vencido);
    const activos = enriched.filter((c) => c.estadoNorm === "Activo");
    return {
      totalMonto,
      totalSaldo,
      vencidos,
      proximos,
      activos,
    };
  }, [enriched]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    enriched.forEach((c) => {
      counts[c.estadoNorm] = (counts[c.estadoNorm] || 0) + 1;
    });
    return counts;
  }, [enriched]);

const totalSocios = resumenSocios?.total ?? resumenSocios?.cantidad ?? "—";
  const sociosActivos = resumenSocios?.activos ?? resumenSocios?.activos_count ?? null;
  const sociosInactivos = resumenSocios?.inactivos ?? resumenSocios?.inactivos_count ?? null;
  const recaudoTotal =
    typeof totalRecaudado?.total === "number"
      ? totalRecaudado.total
      : typeof totalRecaudado === "number"
      ? totalRecaudado
      : null;
  const totalCajas = cajasResumen?.total ?? null;
  const cajasOcupadas = cajasResumen?.ocupadas ?? null;
  const cajasDisponibles = cajasResumen?.disponibles ?? null;
  const cajasOcupacionPct = cajasResumen?.ocupacionPorciento ?? null;
  const deudaTotal = porcentajeDeuda?.deudaImporte ?? null;
  const deudaPct = porcentajeDeuda?.deudaPorciento ?? null;
  const deudaBase = porcentajeDeuda?.totalImporte ?? null;

  const categoriasData = useMemo(() => {
    if (!categoriasSocios?.length) return { categorias: [], data: [] };
    const mapped = categoriasSocios.map((cat: any) => ({
      nombre: cat?.categoria || cat?.nombre || "—",
      cantidad: Number(cat?.cantidad ?? cat?.total ?? 0),
    }));
    return {
      categorias: mapped.map((m) => m.nombre),
      data: mapped.map((m) => m.cantidad),
    };
  }, [categoriasSocios]);

  const pagosSeries = useMemo(() => {
    if (!pagosVsImpagos?.length) return { categorias: [], pagadas: [], impagas: [] };
    const categorias = pagosVsImpagos.map((mes: any, idx: number) => {
      return mes?.mes || mes?.mesNombre || `Mes ${idx + 1}`;
    });
    const pagadas = pagosVsImpagos.map((mes: any) =>
      Number(mes?.pagado ?? mes?.pagadas ?? mes?.pagos ?? 0)
    );
    const impagas = pagosVsImpagos.map((mes: any) =>
      Number(mes?.impago ?? mes?.impagas ?? mes?.pendientes ?? 0)
    );
    return { categorias, pagadas, impagas };
  }, [pagosVsImpagos]);

  const ocupacionTamanoSeries = useMemo(() => {
    if (!ocupacionPorTamano?.length) return { categorias: [], ocupadas: [], disponibles: [] };
    const categorias = ocupacionPorTamano.map((t: any) => t?.tamanoNombre || `Tamaño ${t?.tamanoId}`);
    const ocupadas = ocupacionPorTamano.map((t: any) => Number(t?.ocupadas ?? 0));
    const disponibles = ocupacionPorTamano.map((t: any) => Number(t?.disponibles ?? 0));
    return { categorias, ocupadas, disponibles };
  }, [ocupacionPorTamano]);

  const deudaMesSeries = useMemo(() => {
    if (!deudaPorMes?.length) return { categorias: [], deuda: [] };
    const categorias = deudaPorMes.map((m: any) => `Mes ${m?.periodo ?? ""}`.trim());
    const deuda = deudaPorMes.map((m: any) => Number(m?.deudaImporte ?? 0));
    return { categorias, deuda };
  }, [deudaPorMes]);

  const contratosStatusData = useMemo(() => {
    const entries = Object.entries(statusCounts);
    return entries.map(([estado, count]) => ({ name: estado, y: count }));
  }, [statusCounts]);

  const sociosCategoriaOptions = useMemo<Highcharts.Options>(() => {
    return {
      chart: {
        type: "bar",
        height: 280,
        backgroundColor: "transparent",
      },
      title: { text: undefined },
      xAxis: {
        categories: categoriasData.categorias,
        title: { text: undefined },
        labels: { style: { fontSize: "11px" } },
      },
      yAxis: {
        min: 0,
        title: { text: undefined },
        gridLineColor: "#e5e7eb",
      },
      legend: { enabled: false },
      tooltip: {
        pointFormat: "<b>{point.y}</b> socios",
      },
      series: [
        {
          type: "bar",
          name: "Socios",
          data: categoriasData.data,
          color: "#6366f1",
        },
      ],
      credits: { enabled: false },
    };
  }, [categoriasData]);

  const pagosOptions = useMemo<Highcharts.Options>(() => {
    return {
      chart: {
        type: "column",
        height: 280,
        backgroundColor: "transparent",
      },
      title: { text: undefined },
      xAxis: {
        categories: pagosSeries.categorias,
        labels: { style: { fontSize: "11px" } },
      },
      yAxis: {
        min: 0,
        title: { text: undefined },
        gridLineColor: "#e5e7eb",
      },
      legend: { align: "right", verticalAlign: "top" },
      plotOptions: {
        column: {
          stacking: "normal",
          borderRadius: 4,
        },
      },
      tooltip: {
        shared: true,
      },
      series: [
        {
          type: "column",
          name: "Pagadas",
          data: pagosSeries.pagadas,
          color: "#10b981",
        },
        {
          type: "column",
          name: "Impagas",
          data: pagosSeries.impagas,
          color: "#f97316",
        },
      ],
      credits: { enabled: false },
    };
  }, [pagosSeries]);

  const statusOptions = useMemo<Highcharts.Options>(() => {
    return {
      chart: {
        type: "pie",
        height: 260,
        backgroundColor: "transparent",
      },
      title: { text: undefined },
      tooltip: {
        pointFormat: "<b>{point.y}</b> contratos",
      },
      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: { enabled: true, format: "{point.name}: {point.y}" },
        },
      },
      series: [
        {
          type: "pie",
          name: "Contratos",
          data: contratosStatusData,
        },
      ],
      credits: { enabled: false },
    };
  }, [contratosStatusData]);

  const ocupacionTamanoOptions = useMemo<Highcharts.Options>(() => {
    return {
      chart: {
        type: "bar",
        height: 280,
        backgroundColor: "transparent",
      },
      title: { text: undefined },
      xAxis: {
        categories: ocupacionTamanoSeries.categorias,
        labels: { style: { fontSize: "11px" } },
      },
      yAxis: {
        min: 0,
        title: { text: undefined },
        gridLineColor: "#e5e7eb",
      },
      legend: { align: "right", verticalAlign: "top" },
      plotOptions: {
        bar: {
          stacking: "normal",
          borderRadius: 4,
        },
      },
      tooltip: { shared: true },
      series: [
        {
          type: "bar",
          name: "Ocupadas",
          data: ocupacionTamanoSeries.ocupadas,
          color: "#6366f1",
        },
        {
          type: "bar",
          name: "Disponibles",
          data: ocupacionTamanoSeries.disponibles,
          color: "#94a3b8",
        },
      ],
      credits: { enabled: false },
    };
  }, [ocupacionTamanoSeries]);

  const deudaMesOptions = useMemo<Highcharts.Options>(() => {
    return {
      chart: {
        type: "line",
        height: 260,
        backgroundColor: "transparent",
      },
      title: { text: undefined },
      xAxis: {
        categories: deudaMesSeries.categorias,
        labels: { style: { fontSize: "11px" } },
      },
      yAxis: {
        min: 0,
        title: { text: undefined },
        gridLineColor: "#e5e7eb",
      },
      legend: { enabled: false },
      tooltip: {
        pointFormat: "<b>{point.y}</b>",
      },
      series: [
        {
          type: "line",
          name: "Deuda",
          data: deudaMesSeries.deuda,
          color: "#ef4444",
        },
      ],
      credits: { enabled: false },
    };
  }, [deudaMesSeries]);

  const renderPagination = () => {
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Mostrando{" "}
              <span className="font-medium">
                {filtrados.length === 0 ? 0 : startIndex + 1}
              </span>{" "}
              a <span className="font-medium">{Math.min(endIndex, filtrados.length)}</span> de{" "}
              <span className="font-medium">{filtrados.length}</span> resultados
            </p>
          </div>
          <div>
            <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 text-gray-400 rounded-l-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Anterior</span>
                <ChevronLeft className="h-5 w-5" />
              </button>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                    currentPage === index + 1
                      ? "z-10 bg-blue-600 border-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-50 border-gray-300 bg-white"
                  } border`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 text-gray-400 rounded-r-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Siguiente</span>
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const cardTone = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  } satisfies Record<ResumenCard["tone"], string>;

  const ResumenCardComponent = ({ title, value, subtitle, icon, tone }: ResumenCard) => (
    <div
      className={`flex items-start gap-3 rounded-xl border bg-white p-4 shadow-sm ${
        cardTone[tone] || ""
      }`}
    >
      <div className="rounded-lg bg-white/50 p-2">{icon}</div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          {title}
        </p>
        <p className="text-xl font-semibold text-gray-900">{value}</p>
        <p className="text-xs text-gray-600">{subtitle}</p>
      </div>
    </div>
  );

  const resumenCards: ResumenCard[] = [
    {
      title: "Contratos activos",
      value: stats.activos.length,
      subtitle: `${stats.activos.length} vigentes`,
      icon: <CheckCircle2 className="h-5 w-5" />,
      tone: "emerald",
    },
    {
      title: "Vencidos",
      value: stats.vencidos.length,
      subtitle: stats.vencidos[0]?.fechaFin ? `Último: ${formatDate(stats.vencidos[0].fechaFin)}` : "Sin vencidos",
      icon: <AlertTriangle className="h-5 w-5" />,
      tone: "rose",
    },
    {
      title: "Próximos 30 días",
      value: stats.proximos.length,
      subtitle: stats.proximos[0]?.fechaFin ? `Más próximo: ${formatDate(stats.proximos[0].fechaFin)}` : "Sin vencimientos próximos",
      icon: <Clock3 className="h-5 w-5" />,
      tone: "amber",
    },
    {
      title: "Saldo pendiente",
      value: formatMoney(stats.totalSaldo),
      subtitle: "Total adeudado",
      icon: <TrendingUp className="h-5 w-5" />,
      tone: "indigo",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/inicio"
                className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Link>
            </li>
            <li className="inline-flex items-center">
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">
                Dashboard
              </span>
            </li>
          </ol>
        </nav>

        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Dashboard</h1>
            <p className="text-sm text-gray-600">
              Overview de contratos de cofres, socios y cobranzas.
            </p>
          </div>
        </div>

        {/* KPIs socios y pagos */}
        <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Overview</p>
              <p className="text-sm text-slate-600">Datos generales de socios y cobranzas.</p>
            </div>
          </div>
            <div className="mt-3 grid gap-3 md:grid-cols-4 xl:grid-cols-6">
              <ResumenCardComponent
                title="Socios totales"
                value={totalSocios}
                subtitle="En el sistema"
                icon={<BarChart3 className="h-5 w-5" />}
                tone="indigo"
              />
              <ResumenCardComponent
                title="Activos"
                value={sociosActivos ?? "—"}
                subtitle="Socios con actividad"
                icon={<CheckCircle2 className="h-5 w-5" />}
                tone="emerald"
              />
              <ResumenCardComponent
                title="Inactivos"
                value={sociosInactivos ?? "—"}
                subtitle="Sin actividad"
                icon={<AlertTriangle className="h-5 w-5" />}
                tone="rose"
              />
              <ResumenCardComponent
                title="Total recaudado"
                value={recaudoTotal != null ? formatMoney(recaudoTotal) : "—"}
                subtitle="Acumulado"
                icon={<TrendingUp className="h-5 w-5" />}
                tone="indigo"
              />
              <ResumenCardComponent
                title="Cajas ocupadas"
                value={cajasOcupadas ?? "—"}
                subtitle={`Disponibles: ${cajasDisponibles ?? "—"}`}
                icon={<Building2 className="h-5 w-5" />}
                tone="amber"
              />
              <ResumenCardComponent
                title="Ocupación cajas"
                value={cajasOcupacionPct != null ? `${cajasOcupacionPct}%` : "—"}
                subtitle={`Total: ${totalCajas ?? "—"}`}
                icon={<BarChart3 className="h-5 w-5" />}
                tone="indigo"
              />
            </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/70 p-4 shadow-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Filtros</p>
              <p className="text-xs text-slate-500">
                Se aplican a pagos vs impagos, deuda por mes y porcentaje de deuda.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <span>Año</span>
                <select
                  value={anioPagos}
                  onChange={(e) => setAnioPagos(Number(e.target.value))}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
                >
                  {[anioPagos - 2, anioPagos - 1, anioPagos, anioPagos + 1].map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span>Periodo</span>
                  <select
                    value={periodoDeuda}
                    onChange={(e) =>
                      setPeriodoDeuda(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
                  >
                    <option value="">Todos</option>
                    {Array.from({ length: 12 }).map((_, idx) => (
                      <option key={idx + 1} value={idx + 1}>
                        Mes {idx + 1}
                      </option>
                    ))}
                  </select>
                </div>
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-900">Socios por categoría</p>
                <span className="text-xs text-slate-500">Distribución</span>
              </div>
              {categoriasSocios?.length ? (
                <HighchartsReact highcharts={Highcharts} options={sociosCategoriaOptions} />
              ) : (
                <p className="text-sm text-slate-500">Sin datos de categorías.</p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Pagos vs impagos</p>
                  <p className="text-xs text-slate-500">Cuotas pagadas vs impagas</p>
                </div>
                <span className="text-xs text-slate-500">Año {anioPagos}</span>
              </div>
              {pagosVsImpagos?.length ? (
                <HighchartsReact highcharts={Highcharts} options={pagosOptions} />
              ) : (
                <p className="text-sm text-slate-500">Sin datos de pagos para este año.</p>
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-900">Ocupación por tamaño</p>
                <span className="text-xs text-slate-500">Cajas</span>
              </div>
              {ocupacionPorTamano?.length ? (
                <HighchartsReact highcharts={Highcharts} options={ocupacionTamanoOptions} />
              ) : (
                <p className="text-sm text-slate-500">Sin datos de ocupación.</p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Deuda por mes</p>
                  <p className="text-xs text-slate-500">Evolución anual</p>
                </div>
                <span className="text-xs text-slate-500">Año {anioPagos}</span>
              </div>
              {deudaPorMes?.length ? (
                <HighchartsReact highcharts={Highcharts} options={deudaMesOptions} />
              ) : (
                <p className="text-sm text-slate-500">Sin datos de deuda.</p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-900">Porcentaje de deuda</p>
                <span className="text-xs text-slate-500">
                  {periodoDeuda ? `Mes ${periodoDeuda}` : "Periodo completo"}
                </span>
              </div>
              <div className="grid gap-3">
                <ResumenCardComponent
                  title="Deuda %"
                  value={deudaPct != null ? `${deudaPct}%` : "—"}
                  subtitle="Sobre el total"
                  icon={<AlertTriangle className="h-5 w-5" />}
                  tone="rose"
                />
                <ResumenCardComponent
                  title="Deuda $"
                  value={deudaTotal != null ? formatMoney(deudaTotal) : "—"}
                  subtitle={`Total: ${deudaBase != null ? formatMoney(deudaBase) : "—"}`}
                  icon={<TrendingUp className="h-5 w-5" />}
                  tone="amber"
                />
              </div>
            </div>
          </div>
        </div>

        {error && <p className="text-red-600 mb-4">{error}</p>}

        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 mb-4">
          {resumenCards.map((card) => (
            <ResumenCardComponent
              key={card.title}
              title={card.title}
              value={card.value}
              subtitle={card.subtitle}
              icon={card.icon}
              tone={card.tone}
            />
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_0.7fr] mb-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-800">Contratos por estado</p>
              <BarChart3 className="h-5 w-5 text-gray-500" />
            </div>
            {Object.keys(statusCounts).length ? (
              <HighchartsReact highcharts={Highcharts} options={statusOptions} />
            ) : (
              <p className="text-xs text-gray-500">Sin contratos para mostrar.</p>
            )}
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-amber-800 font-semibold">
              <Clock3 className="h-5 w-5" />
              Próximos a vencer (30 días)
            </div>
            <div className="mt-3 space-y-2">
              {stats.proximos.slice(0, 4).map((c) => (
                <div key={c.id} className="rounded-lg border border-amber-100 bg-white px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-800">
                      {c.socioNombre || `Contrato ${c.id}`}
                    </span>
                    <span className="text-xs text-gray-600">
                      {c.diffDias !== null ? `${c.diffDias} días` : ""}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Caja {c.cajaNumero || "—"} · Vence: {formatDate(c.fechaFin)}
                  </p>
                </div>
              ))}
              {stats.proximos.length === 0 && (
                <p className="text-xs text-gray-600">No hay vencimientos próximos.</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={estadoFiltro}
                onChange={(e) => {
                  setEstadoFiltro(e.target.value);
                  setCurrentPage(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="todos">Todos los estados</option>
                {Array.from(new Set(enriched.map((c) => c.estadoNorm))).map((est) => (
                  <option key={est} value={est}>
                    {est}
                  </option>
                ))}
              </select>
              <input
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setCurrentPage(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Buscar socio, id o caja"
              />
            </div>
            <div className="text-xs text-gray-600">
              {filtrados.length} contrato(s) · Saldo pendiente: {formatMoney(stats.totalSaldo)} ·
              Monto total: {formatMoney(stats.totalMonto)}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Socio
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Caja
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Inicio
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fin
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Saldo
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentContratos.map((c) => (
                  <tr
                    key={c.id}
                    className={`hover:bg-gray-50 ${
                      c.vencido ? "bg-rose-50" : ""
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                      <div className="flex items-center gap-2">
                        <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div>
                          <div>{c.socioNombre || `Contrato ${c.id}`}</div>
                          <div className="text-xs text-gray-500">
                            ID socio: {c.socioId || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">Caja {c.cajaNumero || "—"}</td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                          c.estadoNorm === "Vencido"
                            ? "bg-rose-100 text-rose-700"
                            : c.estadoNorm === "Pendiente"
                            ? "bg-amber-100 text-amber-700"
                            : c.estadoNorm === "Finalizado"
                            ? "bg-gray-100 text-gray-700"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {c.estadoNorm}
                        {c.vencido && <AlertTriangle className="h-3 w-3" />}
                        {c.proximo && !c.vencido && <Clock3 className="h-3 w-3" />}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(c.fechaInicio)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(c.fechaFin)}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 font-semibold">
                      {formatMoney(c.monto)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{formatMoney(c.saldoPendiente)}</td>
                  </tr>
                ))}
                {currentContratos.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-600">
                      No hay contratos que coincidan con el filtro.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loading />
          </div>
        )}

        {dashboardLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 backdrop-blur-sm">
            <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-center gap-3">
                <span className="h-3 w-3 animate-pulse rounded-full bg-indigo-500" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Actualizando dashboard</p>
                  <p className="text-xs text-slate-500">Aplicando filtros y refrescando gráficos…</p>
                </div>
              </div>
              <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full w-1/2 animate-[pulse_1.2s_ease-in-out_infinite] rounded-full bg-indigo-500" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
